import { useMemo, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PvComparisonSummary from "./pvGeneration/PvComparisonSummary";
import PvKpiCards from "./pvGeneration/PvKpiCards";
import PvGenerationChart from "./pvGeneration/PvGenerationChart";
import PvRunHistoryTable from "./pvGeneration/PvRunHistoryTable";
import { computeAccuracyMetrics, computeSeriesEnergyMwh, seriesMaxDifferenceKw, seriesPeakKw } from "./pvGeneration/pvGenerationUtils";

const RUN_TYPE_OPTIONS = [
  { key: "forecast", label: "Forecast weather" },
  { key: "actual_weather", label: "Historical weather" },
];

export default function PvGenerationPanel({
  projectId,
  runType,
  onRunTypeChange,
  onOpenRangePicker,
  rangeLabelNode,
  onTrigger,
  triggering,
  triggerError,
  runs,
  runsLoading,
  runsError,
  selectedRunIds,
  onToggleRun,
  seriesByRun,
  seriesLoadingIds,
  historicalRun,
  forecastRuns,
  measuredSeries,
  measuredAvailable,
  measuredLoading,
  measuredError,
  onExport,
  onExportRun,
  onRerun,
  exporting,
  exportError,
  onFullscreen,
  timeZone,
}) {
  const historyRef = useRef(null);
  const pendingCount = runs.filter((r) => r.status === "queued" || r.status === "running").length;

  const historicalSeries = historicalRun ? seriesByRun[historicalRun.run_id] : null;
  const forecastEntries = useMemo(
    () => forecastRuns.map((run) => ({ run, series: seriesByRun[run.run_id] || null })),
    [forecastRuns, seriesByRun]
  );

  const historicalMwh = useMemo(
    () => (historicalSeries ? computeSeriesEnergyMwh(historicalSeries) : null),
    [historicalSeries]
  );
  const historicalPeakKw = useMemo(
    () => (historicalSeries ? seriesPeakKw(historicalSeries) : null),
    [historicalSeries]
  );

  const forecastRows = useMemo(
    () =>
      forecastEntries.map(({ run, series }) => {
        const forecastMwh = series ? computeSeriesEnergyMwh(series) : null;
        const differenceMwh = historicalMwh != null && forecastMwh != null ? forecastMwh - historicalMwh : null;
        const errorPct = differenceMwh != null && historicalMwh ? (differenceMwh / historicalMwh) * 100 : null;
        const forecastPeakKw = series ? seriesPeakKw(series) : null;
        const peakDifferenceKw =
          historicalPeakKw != null && forecastPeakKw != null ? forecastPeakKw - historicalPeakKw : null;
        const accuracy =
          measuredAvailable && measuredSeries?.length && series
            ? computeAccuracyMetrics(series, measuredSeries)
            : null;
        const maxDifferenceKw = seriesMaxDifferenceKw(series, historicalSeries);
        return { run, forecastMwh, differenceMwh, errorPct, peakDifferenceKw, maxDifferenceKw, accuracy };
      }),
    [forecastEntries, historicalMwh, historicalPeakKw, historicalSeries, measuredAvailable, measuredSeries]
  );

  const handleChangeRun = () => {
    historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Stack spacing={3}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              PV Generation Estimate
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Estimate and compare PV production using forecast, historical, or SCADA data.
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", md: "center" }}
            flexWrap="wrap"
            sx={{ rowGap: 1 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Run on:
            </Typography>
            <Stack direction="row" spacing={1}>
              {RUN_TYPE_OPTIONS.map((opt) => (
                <Chip
                  key={opt.key}
                  label={opt.label}
                  color={runType === opt.key ? "primary" : "default"}
                  variant={runType === opt.key ? "filled" : "outlined"}
                  onClick={() => onRunTypeChange(opt.key)}
                  sx={{ fontWeight: 700 }}
                />
              ))}
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={onOpenRangePicker}
              startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
              endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
              sx={{ minWidth: 180, textTransform: "none" }}
            >
              {rangeLabelNode}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={triggering ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon fontSize="small" />}
              disabled={triggering || !projectId}
              onClick={onTrigger}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Estimate Generation
            </Button>
          </Stack>

          {!projectId && (
            <Alert severity="info">Select a project to trigger and review PV generation runs.</Alert>
          )}
          {triggerError && <Alert severity="error">{triggerError}</Alert>}
          {pendingCount > 0 && (
            <Alert severity="info" icon={<CircularProgress size={18} />}>
              Running...
            </Alert>
          )}
          {runsError && <Alert severity="error">{runsError}</Alert>}
          {!runsError && !runsLoading && runs.length === 0 && (
            <Alert severity="info">
              PV generation data not available yet. Please select a date range and run a PV
              generation estimation.
            </Alert>
          )}
        </Stack>

        {(runsLoading || runs.length > 0) && !runsError && (
          <>
            <Divider sx={{ my: 2 }} />
            {runsLoading && runs.length === 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} /> Loading runs...
              </Box>
            ) : (
              <Stack spacing={2}>
                <PvComparisonSummary
                  historicalRun={historicalRun}
                  forecastRuns={forecastRuns}
                  onChangeRun={handleChangeRun}
                  onRemoveForecastRun={onToggleRun}
                  measuredAvailable={measuredAvailable}
                  measuredLoading={measuredLoading}
                  measuredError={measuredError}
                  timeZone={timeZone}
                />

                <PvKpiCards
                  historicalMwh={historicalMwh}
                  forecastRows={forecastRows}
                  measuredAvailable={measuredAvailable}
                  timeZone={timeZone}
                />

                <Box>
                  <PvGenerationChart
                    historicalRun={historicalRun}
                    forecastEntries={forecastEntries}
                    historicalSeries={historicalSeries}
                    measuredSeries={measuredSeries}
                    measuredAvailable={measuredAvailable}
                    historicalMwh={historicalMwh}
                    forecastRows={forecastRows}
                    timeZone={timeZone}
                    onFullscreen={onFullscreen}
                    onExportComparisonExcel={onExport}
                    onExportRun={onExportRun}
                    exporting={exporting}
                    exportError={exportError}
                  />
                </Box>

                <Box ref={historyRef}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: "0.04em", mb: 1 }}>
                    RUN HISTORY
                  </Typography>
                  <PvRunHistoryTable
                    runs={runs}
                    selectedRunIds={selectedRunIds}
                    onToggleRun={onToggleRun}
                    seriesLoadingIds={seriesLoadingIds}
                    onExportRun={onExportRun}
                    onRerun={onRerun}
                    timeZone={timeZone}
                  />
                </Box>
              </Stack>
            )}
          </>
        )}
      </Paper>
    </Stack>
  );
}
